import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiPublic } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SeoLayout from "@/components/SeoLayout";
import { SeoMeta } from "@/components/Jsonld";
import PaymentSummary from "@/components/PaymentSummary";
import { Clock, MapPin, User, Wrench, Search, PackageOpen } from "lucide-react";

const STATUS_COPY = {
  new: { label: "Booking received", className: "bg-cream/10 text-cream/80 border-cream/20" },
  scheduled: { label: "Scheduled", className: "bg-cream/10 text-cream border-cream/20" },
  "en-route": { label: "Tech en-route", className: "bg-orange/15 text-orange border-orange/40" },
  "on-site": { label: "On site", className: "bg-orange/15 text-orange border-orange/40" },
  "part-ordered": { label: "Part ordered — waiting", className: "bg-orange/15 text-orange border-orange/40" },
  completed: { label: "Completed", className: "bg-gold/15 text-gold border-gold/40" },
  cancelled: { label: "Cancelled", className: "bg-cream/10 text-cream/40 border-cream/15" },
};

export default function Track() {
  const [params] = useSearchParams();
  const [phone, setPhone] = useState(params.get("phone") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function lookup(p) {
    if (!p) return;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const res = await apiPublic.get(`/track?phone=${encodeURIComponent(p)}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "No bookings found for that number.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (params.get("phone")) lookup(params.get("phone"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SeoLayout>
      <SeoMeta title="Track Your Repair · Space Appliance Repair San Antonio" description="Check the live status of your appliance repair in San Antonio. Enter your phone to see tech ETA, scheduled window, and job progress." />
      <section className="max-w-2xl mx-auto px-5 sm:px-8 py-14">
        <div className="chip"><Search className="w-3.5 h-3.5" /> Booking tracker</div>
        <h1 className="display-retro mt-5 text-cream text-4xl sm:text-5xl normal-case font-semibold leading-[1.05]">
          Your repair <span className="text-orange">status</span><span className="text-gold">.</span>
        </h1>
        <p className="mt-4 text-cream/75">Enter the phone number you booked with.</p>

        <div className="mt-7 flex gap-2 items-stretch">
          <div className="flex-1">
            <Label className="sr-only">Phone</Label>
            <Input
              data-testid="track-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup(phone)}
              placeholder="(210) 555-0199"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              style={{ color: "#0B192C", caretColor: "#E65C00", WebkitTextFillColor: "#0B192C" }}
              className="h-12 bg-white border-2 border-[#E65C00]/40 text-base font-bold tracking-wide placeholder:text-[#0B192C]/35 focus-visible:ring-[#E65C00] focus-visible:ring-2 focus-visible:border-[#E65C00]"
            />
          </div>
          <button
            data-testid="track-lookup-btn"
            onClick={() => lookup(phone)}
            className="btn-orange label-retro text-sm h-12 px-6"
          >
            {loading ? "…" : "Look up"}
          </button>
        </div>

        {error && (
          <div className="mt-7 text-sm text-orange bg-orange/10 border border-orange/40 rounded-md p-4">{error}</div>
        )}

        {data?.pending_leads?.length > 0 && (
          <div className="mt-10 space-y-3">
            <div className="text-xs uppercase tracking-widest text-orange label-retro">Pending requests</div>
            {data.pending_leads.map((l) => (
              <div key={l.id} className="bg-navy-2/70 border border-cream/15 rounded-md p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="display-retro text-cream text-lg capitalize">{l.appliance}</div>
                    <div className="text-sm text-cream/65 mt-1">{l.issue || "Booking received"}</div>
                    <div className="text-xs text-cream/50 mt-2">{l.address || "—"}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest border bg-cream/10 text-cream/80 border-cream/20 px-3 py-1 rounded-full">{l.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {data?.jobs?.length > 0 && (
          <div className="mt-10 space-y-4">
            <div className="text-xs uppercase tracking-widest text-orange label-retro">Jobs</div>
            {data.jobs.map((j) => {
              const meta = STATUS_COPY[j.status] || STATUS_COPY.new;
              return (
                <div key={j.id} data-testid={`track-job-${j.id}`} className="bg-navy-2/70 border border-cream/15 rounded-md p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="display-retro text-cream text-xl capitalize">{j.appliance}</div>
                      <div className="text-sm text-cream/65 mt-1">{j.issue || "—"}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${meta.className}`}>{meta.label}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-5 text-sm">
                    {j.technician && (
                      <div className="flex items-center gap-2 text-cream/85">
                        <User className="w-4 h-4 text-orange" /> {j.technician.name}
                      </div>
                    )}
                    {j.scheduled_for && (
                      <div className="flex items-center gap-2 text-cream/85">
                        <Clock className="w-4 h-4 text-orange" /> {j.scheduled_for}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-cream/85 sm:col-span-2">
                      <MapPin className="w-4 h-4 text-orange" /> {j.address}
                    </div>
                    {j.status === "part-ordered" && (
                      <div className="flex items-start gap-2 text-orange sm:col-span-2 text-sm">
                        <PackageOpen className="w-4 h-4 mt-0.5" />
                        <span>We&apos;re waiting on a part for your appliance. We&apos;ll be back as soon as it arrives.</span>
                      </div>
                    )}
                    {j.amount_due != null && !j.payment && (
                      <div className="flex items-center gap-2 text-cream/85 sm:col-span-2">
                        <Wrench className="w-4 h-4 text-gold" /> Amount due: <span className="num font-medium text-gold">${Number(j.amount_due).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  {j.payment && (
                    <div className="mt-5">
                      <PaymentSummary summary={j.payment} theme="light" testid={`track-payment-${j.id}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center text-xs label-retro text-cream/50 uppercase tracking-widest">
          Need to book a new repair? <Link to="/" className="text-orange hover:text-orange-2" data-testid="track-new-book-link">Go to mission control →</Link>
        </div>
      </section>
    </SeoLayout>
  );
}
